import { apiCall, axiosInstance, getConfig, serverUrl } from "./Utils";
import type { ApiResult } from "../types/api";

export type NotificationType =
    | "BUG_REPORT" | "FEATURE_REQUEST" | "TASK" | "CONTACT" | "USER_CREATION" | "PASSWORD_RESET" | "INFRA_ALERT";

export type AlertSeverity = "info" | "warning" | "critical";
export type NotificationStatus = "TODO" | "IN_PROGRESS" | "TO_TEST" | "DONE";
export type TicketPriority = "LOW" | "NORMAL" | "HIGH" | "URGENT";

/** Signalement archivé (rapport de bug ou message de contact). */
export interface NotificationRecord {
    id: string;
    type: NotificationType;
    status: NotificationStatus;
    createdAt?: number;
    updatedAt?: number;
    // Ticket (titre court + priorité)
    title?: string;
    priority?: TicketPriority;
    // Payload bug-report
    username?: string;
    path?: string;
    pageStatus?: string;
    description?: string;
    details?: string;
    contactFirstName?: string;
    contactLastName?: string;
    contactEmail?: string;
    // Payload contact
    email?: string;
    subject?: string;
    content?: string;
    // Capture (référence dans cms-images)
    screenshotFilename?: string;
    // Images jointes (références cms-images)
    attachmentFilenames?: string[];
    // Traçabilité de clôture
    resolutionNote?: string;
    resolvedBy?: string;
    resolvedAt?: number;
    // Suivi du changement de statut côté déclarant
    statusChangedAt?: number;
    previousStatus?: NotificationStatus;
    // Fil de discussion déclarant ↔ équipe
    comments?: NotificationComment[];
    lastCommentAt?: number;
    // Alerte d'infrastructure (INFRA_ALERT, relayée par Alertmanager)
    alertFingerprint?: string;
    alertName?: string;
    alertSeverity?: AlertSeverity;
    alertState?: "firing" | "resolved";
    alertLabels?: Record<string, string>;
    alertStartsAt?: number;
    alertEndsAt?: number;
}

/** Contenu d'un ticket à créer ou réécrire depuis le kanban. */
export interface TicketInput {
    type?: "BUG_REPORT" | "FEATURE_REQUEST" | "TASK";
    status?: NotificationStatus;
    title?: string;
    priority?: TicketPriority;
    description?: string;
    details?: string;
    path?: string;
    /** Nouvelles images, en data-URL */
    attachmentsBase64?: string[];
    /** Édition : images existantes à conserver */
    attachmentFilenames?: string[];
}

/** Brouillon soumis à l'assistant IA de rédaction. */
export interface AiTicketRequest {
    type?: string;
    title?: string;
    description?: string;
    details?: string;
    priority?: string;
    path?: string;
    answers?: { question: string; answer: string }[];
    imagesBase64?: string[];
    finalize?: boolean;
}

/** Proposition de l'assistant IA. `error` renseigné = échec (autres champs vides). */
export interface AiTicketResponse {
    type?: "BUG_REPORT" | "FEATURE_REQUEST" | "TASK";
    title?: string;
    description?: string;
    details?: string;
    priority?: TicketPriority;
    questions: string[];
    model?: string;
    error?: string;
}

/** Réglages de l'assistant IA. `apiKey` est en écriture seule (vide = conserver). */
export interface AiAssistSettings {
    enabled: boolean;
    baseUrl?: string;
    model?: string;
    apiKey?: string | null;
    apiKeySet?: boolean;
    apiKeyHint?: string | null;
}

/** Résultat d'un test de la passerelle LLM ou de la liste de ses modèles. */
export interface AiAssistTest {
    ok: boolean;
    error?: string;
    model?: string;
    reply?: string;
    latencyMs?: number;
    models?: string[];
}

/** Webhook sortant de relais des alertes. `url` est en écriture seule (null = conserver). */
export interface AlertWebhook {
    id?: string;
    name: string;
    kind: "DISCORD" | "SLACK" | "GENERIC";
    url?: string | null;
    /** Lecture seule : fin de l'URL enregistrée. */
    urlHint?: string | null;
    enabled: boolean;
    minSeverity: AlertSeverity;
}

/** Canaux de relais des alertes d'infrastructure (email + webhooks). */
export interface AlertChannels {
    emailEnabled: boolean;
    smtpHost?: string;
    smtpPort?: number;
    smtpSecurity?: "NONE" | "STARTTLS" | "SSL";
    smtpUsername?: string;
    /** Écriture seule : vide = conserver le mot de passe enregistré. */
    smtpPassword?: string | null;
    /** Lecture seule. */
    smtpPasswordSet?: boolean;
    emailFrom?: string;
    emailTo: string[];
    emailMinSeverity: AlertSeverity;
    webhooks: AlertWebhook[];
    notifyResolved: boolean;
    grafanaUrl?: string;
}

/** Auteur d'un commentaire : le déclarant, ou l'équipe. */
export type CommentAuthorRole = "REPORTER" | "ADMIN";

/** Message du fil de discussion d'un signalement. */
export interface NotificationComment {
    id: string;
    author?: string;
    authorRole: CommentAuthorRole;
    message: string;
    createdAt?: number;
}

export interface NotificationStatusUpdate {
    status: NotificationStatus;
    resolutionNote?: string;
    resolvedBy?: string;
}

/** Types d'événements souscriptibles. */
export const NOTIFICATION_TYPES: NotificationType[] | string[] = [
    "BUG_REPORT", "FEATURE_REQUEST", "USER_CREATION", "CONTACT",
];

/** Préférences de souscription in-app d'un admin (cloche). */
export interface NotificationSubscription {
    admin?: string;
    types: string[];
    lastSeenAt?: number;
    /** Dernière consultation du volet « mes signalements » (suivi côté déclarant). */
    reporterLastSeenAt?: number;
}

/**
 * Liste des signalements archivés (du plus récent au plus ancien).
 * Endpoint ADMIN : GET /api/cms/notifications/list
 * (chemin /list explicite pour éviter la collision RESTEasy avec /cms/{id})
 */
async function getNotifications(): Promise<ApiResult<NotificationRecord[]>> {
    return apiCall(
        axiosInstance.get(`${serverUrl}/api/cms/notifications/list`, getConfig())
    );
}

async function getSubTasks(parentId: string): Promise<ApiResult<SubTaskRecord[]>> {
    return apiCall(
        axiosInstance.get(
            `${serverUrl}/api/cms/notifications/${encodeURIComponent(parentId)}/subtasks`,
            getConfig()
        )
    );
}

async function createSubTask(
    parentId: string,
    dto: SubTaskCreate
): Promise<ApiResult<SubTaskRecord>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/${encodeURIComponent(parentId)}/subtasks`,
            dto,
            getConfig()
        )
    );
}

async function updateSubTask(
    id: string,
    dto: SubTaskUpdate
): Promise<ApiResult<SubTaskRecord>> {
    return apiCall(
        axiosInstance.put(
            `${serverUrl}/api/cms/notifications/subtasks/${encodeURIComponent(id)}`,
            dto,
            getConfig()
        )
    );
}

async function deleteSubTask(id: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.delete(
            `${serverUrl}/api/cms/notifications/subtasks/${encodeURIComponent(id)}`,
            getConfig()
        )
    );
}

async function addSubTaskLog(
    id: string,
    content: string
): Promise<ApiResult<SubTaskRecord>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/subtasks/${encodeURIComponent(id)}/logs`,
            { content },
            getConfig()
        )
    );
}

/**
 * Met à jour le statut de suivi d'un signalement (et déclare la résolution).
 * Endpoint ADMIN : POST /api/cms/notifications/{id}/status
 */
async function updateStatus(id: string, update: NotificationStatusUpdate): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/${id}/status`, update, getConfig())
    );
}

/**
 * Supprime définitivement un signalement archivé.
 * Endpoint ADMIN : DELETE /api/cms/notifications/{id}
 */
async function deleteNotification(id: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.delete(`${serverUrl}/api/cms/notifications/${id}`, getConfig())
    );
}

/**
 * Signalements déposés par l'utilisateur courant (login et/ou email).
 * Endpoint utilisateur connecté : GET /api/cms/notifications/mine?user=…&email=…
 */
async function getMyNotifications(user: string, email?: string): Promise<ApiResult<NotificationRecord[]>> {
    const qs = `user=${encodeURIComponent(user || "")}&email=${encodeURIComponent(email || "")}`;
    return apiCall(
        axiosInstance.get(`${serverUrl}/api/cms/notifications/mine?${qs}`, getConfig())
    );
}

/** Marque le volet « mes signalements » comme consulté. */
async function markMineSeen(user: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/mine/seen?user=${encodeURIComponent(user)}`,
            {},
            getConfig()
        )
    );
}

/**
 * Ajoute un commentaire au fil d'un signalement, en tant que déclarant.
 * Endpoint utilisateur connecté : POST /api/cms/notifications/{id}/comments
 *
 * Renvoie le signalement à jour (fil complet), pas besoin de recharger la liste.
 */
async function addComment(id: string, author: string, message: string): Promise<ApiResult<NotificationRecord>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/${id}/comments?author=${encodeURIComponent(author || "")}`,
            { message },
            getConfig()
        )
    );
}

/**
 * Ajoute un commentaire en tant qu'équipe (route séparée protégée ADMIN — le rôle
 * n'est jamais transmis dans le corps de la requête, il est figé par l'endpoint).
 * Endpoint ADMIN : POST /api/cms/notifications/{id}/comments/admin
 */
async function addAdminComment(id: string, author: string, message: string): Promise<ApiResult<NotificationRecord>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/${id}/comments/admin?author=${encodeURIComponent(author || "")}`,
            { message },
            getConfig()
        )
    );
}

/** URL publique (cachée) d'une capture d'écran stockée dans cms-images. */
function screenshotUrl(filename?: string): string | null {
    return filename ? `${serverUrl}/api/cms/images/${filename}` : null;
}

/**
 * Souscription in-app de l'admin (types + dernière consultation).
 * Endpoint ADMIN : GET /api/cms/notifications/subscription?admin=…
 */
async function getSubscription(admin: string): Promise<ApiResult<NotificationSubscription>> {
    return apiCall(
        axiosInstance.get(
            `${serverUrl}/api/cms/notifications/subscription?admin=${encodeURIComponent(admin)}`,
            getConfig()
        )
    );
}

/** Enregistre les types souscrits. Endpoint ADMIN : PUT /api/cms/notifications/subscription */
async function saveSubscription(admin: string, types: string[]): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.put(`${serverUrl}/api/cms/notifications/subscription`, { admin, types }, getConfig())
    );
}

/** Marque la cloche comme consultée. Endpoint ADMIN : POST /api/cms/notifications/subscription/seen?admin=… */
async function markSeen(admin: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/subscription/seen?admin=${encodeURIComponent(admin)}`,
            {},
            getConfig()
        )
    );
}

/** Enregistre un abonnement Web Push (navigateur) pour l'admin. */
async function pushSubscribe(admin: string, endpoint: string, p256dh: string, auth: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/push/subscribe`, { admin, endpoint, p256dh, auth }, getConfig())
    );
}

/** Retire l'abonnement Web Push identifié par son endpoint. */
async function pushUnsubscribe(admin: string, endpoint: string): Promise<ApiResult<void>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/push/unsubscribe`, { admin, endpoint }, getConfig())
    );
}

/**
 * Envoie une notification de test aux navigateurs abonnés de l'admin.
 * Répond {sent: n} — n = nombre d'appareils réellement notifiés (0 = aucun).
 */
async function pushTest(admin: string): Promise<ApiResult<{ sent: number }>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/push/test?admin=${encodeURIComponent(admin)}`,
            {},
            getConfig()
        )
    );
}

/** Configuration des canaux d'alerte (secrets masqués). Endpoint ADMIN. */
async function getAlertChannels(): Promise<ApiResult<AlertChannels>> {
    return apiCall(
        axiosInstance.get(`${serverUrl}/api/cms/notifications/alert-channels`, getConfig())
    );
}

/** Enregistre les canaux d'alerte et renvoie la configuration à jour. Endpoint ADMIN. */
async function saveAlertChannels(channels: AlertChannels, by: string): Promise<ApiResult<AlertChannels>> {
    return apiCall(
        axiosInstance.put(
            `${serverUrl}/api/cms/notifications/alert-channels?by=${encodeURIComponent(by || "")}`,
            channels,
            getConfig()
        )
    );
}

/**
 * Envoie une alerte de test sur un canal enregistré (`email` ou l'id d'un webhook).
 * Répond {ok, error} — error porte le message du serveur SMTP / du webhook.
 */
async function testAlertChannel(channel: string): Promise<ApiResult<{ ok: boolean; error: string }>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/alert-channels/test?channel=${encodeURIComponent(channel)}`,
            {},
            getConfig()
        )
    );
}

/**
 * Crée un ticket depuis le kanban (statut initial = colonne choisie).
 * Endpoint ADMIN : POST /api/cms/notifications/ticket?author=…
 */
async function createTicket(ticket: TicketInput, author: string): Promise<ApiResult<NotificationRecord>> {
    return apiCall(
        axiosInstance.post(
            `${serverUrl}/api/cms/notifications/ticket?author=${encodeURIComponent(author || "")}`,
            ticket,
            getConfig()
        )
    );
}

/**
 * Réécrit le contenu d'un ticket (titre, priorité, description, images…).
 * Endpoint ADMIN : POST /api/cms/notifications/{id}/content
 */
async function updateContent(id: string, ticket: TicketInput): Promise<ApiResult<NotificationRecord>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/${id}/content`, ticket, getConfig())
    );
}

/**
 * Fait réécrire un brouillon par l'assistant IA (réponse en 10 à 90 s).
 * Endpoint ADMIN : POST /api/cms/notifications/ai/refine
 */
async function aiRefine(req: AiTicketRequest): Promise<ApiResult<AiTicketResponse>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/ai/refine`, req, { ...getConfig(), timeout: 180000 })
    );
}

/** Réglages de l'assistant IA (clé masquée). Endpoint ADMIN. */
async function getAiSettings(): Promise<ApiResult<AiAssistSettings>> {
    return apiCall(
        axiosInstance.get(`${serverUrl}/api/cms/notifications/ai/settings`, getConfig())
    );
}

/** Enregistre les réglages de l'assistant IA. Endpoint ADMIN. */
async function saveAiSettings(settings: AiAssistSettings, by: string): Promise<ApiResult<AiAssistSettings>> {
    return apiCall(
        axiosInstance.put(
            `${serverUrl}/api/cms/notifications/ai/settings?by=${encodeURIComponent(by || "")}`,
            settings,
            getConfig()
        )
    );
}

/** Teste la passerelle avec les réglages du formulaire (clé vide = clé enregistrée). Endpoint ADMIN. */
async function testAi(settings: AiAssistSettings): Promise<ApiResult<AiAssistTest>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/ai/test`, settings, { ...getConfig(), timeout: 180000 })
    );
}

/** Liste les modèles exposés par la passerelle. Endpoint ADMIN. */
async function listAiModels(settings: AiAssistSettings): Promise<ApiResult<AiAssistTest>> {
    return apiCall(
        axiosInstance.post(`${serverUrl}/api/cms/notifications/ai/models`, settings, getConfig())
    );
}

const ApiNotificationCtrl = {
    createTicket,
    updateContent,
    aiRefine,
    getAiSettings,
    saveAiSettings,
    testAi,
    listAiModels,
    getNotifications,
    getMyNotifications,
    markMineSeen,
    addComment,
    addAdminComment,
    updateStatus,
    deleteNotification,
    screenshotUrl,
    getSubscription,
    saveSubscription,
    markSeen,
    pushSubscribe,
    pushUnsubscribe,
    pushTest,
    getAlertChannels,
    saveAlertChannels,
    testAlertChannel,
    getSubTasks,
    createSubTask,
    updateSubTask,
    deleteSubTask,
    addSubTaskLog,
};

export default ApiNotificationCtrl;

//Sous-tâches
export type SubTaskStatus = "TODO" | "IN_PROGRESS" | "TO_TEST" | "DONE";

export interface SubTaskLog {
    id: string;
    author: string;
    content: string;
    createdAt: number;
}

export interface SubTaskRecord {
    id: string;
    parentId: string;
    title: string;
    description?: string | null;
    status: SubTaskStatus;
    priority?: string | null;
    createdAt: number;
    updatedAt: number;
    logs: SubTaskLog[];
}

export interface SubTaskCreate {
    title: string;
    description?: string;
    priority?: string;
}

export interface SubTaskUpdate {
    title?: string;
    description?: string;
    status?: SubTaskStatus;
    priority?: string;
}